<script setup lang="ts">
// Product of Launch Maniac LLC, Las Vegas, Nevada - (725) 444-8200 support@launchmaniac.com

import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { UserRole } from '../types';

const route = useRoute();
const authStore = useAuthStore();

const isAdmin = computed(() => authStore.userRole === UserRole.ADMIN);

interface NavItem {
  name: string;
  path: string;
  section?: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  // Main
  { name: 'Overview', path: '/', section: 'Main' },
  { name: 'Setup Wizard', path: '/setup', section: 'Main' },

  // CRM
  { name: 'Contacts', path: '/contacts', section: 'CRM' },
  { name: 'Conversations', path: '/conversations', section: 'CRM' },

  // Campaigns
  { name: 'Cohort Builder', path: '/cohort-builder', section: 'Campaigns' },
  { name: 'Segments', path: '/segments', section: 'Campaigns' },
  { name: 'Composer', path: '/composer', section: 'Campaigns' },
  { name: 'Cadence', path: '/cadence', section: 'Campaigns' },
  { name: 'Launch Control', path: '/launch-control', section: 'Campaigns' },

  // Operations
  { name: 'Live Triage', path: '/live-triage', section: 'Operations' },
  { name: 'Hygiene', path: '/hygiene', section: 'Operations' },
  { name: 'Exceptions', path: '/exceptions', section: 'Operations' },

  // Billing
  { name: 'Ledger', path: '/ledger', section: 'Billing' },
  { name: 'Weekly Close', path: '/weekly-close', section: 'Billing' },
  { name: 'Disputes', path: '/disputes', section: 'Billing' },

  // Admin
  { name: 'Suppressions', path: '/suppressions', section: 'Admin' },
  { name: 'Messaging', path: '/admin/messaging', section: 'Admin', adminOnly: true },
  { name: 'A2P Center', path: '/admin/a2p', section: 'Admin', adminOnly: true },
  { name: 'Settings', path: '/settings', section: 'Admin' },
];

const groupedNavItems = computed(() => {
  const groups: Record<string, NavItem[]> = {};

  navItems.forEach(item => {
    // Skip admin-only items for non-admins
    if (item.adminOnly && !isAdmin.value) return;

    const section = item.section || 'Other';
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(item);
  });

  return groups;
});

function isActive(path: string): boolean {
  return route.path === path;
}
</script>

<template>
  <aside class="w-64 bg-sidebar text-white flex flex-col">
    <div class="p-4 border-b border-sidebar-light">
      <div class="flex items-center gap-3">
        <img src="/favicon.svg" alt="Repipe" class="w-10 h-10 rounded-lg">
        <div>
          <h1 class="text-xl font-bold">Repipe</h1>
          <p class="text-sm text-primary-300">Database Reactivation</p>
        </div>
      </div>
    </div>

    <nav class="flex-1 p-4 overflow-y-auto">
      <div v-for="(items, section) in groupedNavItems" :key="section" class="mb-4">
        <p class="text-xs font-semibold text-primary-400 uppercase tracking-wider mb-2 px-4">
          {{ section }}
        </p>
        <ul class="space-y-1">
          <li v-for="item in items" :key="item.path">
            <RouterLink
              :to="item.path"
              :class="[
                'block px-4 py-2 rounded-lg transition-colors text-sm',
                isActive(item.path)
                  ? 'bg-primary-500 text-white'
                  : 'text-primary-200 hover:bg-sidebar-light hover:text-white'
              ]"
            >
              {{ item.name }}
            </RouterLink>
          </li>
        </ul>
      </div>
    </nav>

    <div class="p-4 border-t border-sidebar-light text-xs text-primary-400">
      <p>Launch Maniac LLC</p>
      <p>Las Vegas, Nevada</p>
    </div>
  </aside>
</template>
